import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  TransformComponent,
  TransformWrapper,
  type ReactZoomPanPinchRef,
} from 'react-zoom-pan-pinch'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Matches ChatInput attachment (paperclip) / send squares: primary when active, muted when disabled. */
const chatImagePreviewControlButtonClassName = cn(
  'flex aspect-square h-11 shrink-0 items-center justify-center rounded-[var(--card-radius)] transition-all',
  'bg-primary text-primary-foreground hover:bg-primary/90',
  'disabled:pointer-events-none disabled:bg-muted disabled:text-muted-foreground disabled:hover:bg-muted'
)

/** Matches stacked chat image bubble outer radius — clip via frame. */
const chatImagePreviewImageFrameRadiusClassName = 'rounded-[18px]'

/** Screen-edge horizontal inset aligned with chat (`--min-side-padding`) + safe areas. */
const chatImagePreviewEdgeGutterClassName =
  'pl-[max(var(--min-side-padding),env(safe-area-inset-left))] pr-[max(var(--min-side-padding),env(safe-area-inset-right))]'

type OpenImagePreviewArgs = {
  urls: string[]
  initialIndex?: number
}

type ChatImagePreviewContextValue = {
  openImagePreview: (args: OpenImagePreviewArgs) => void
}

const ChatImagePreviewContext = createContext<ChatImagePreviewContextValue | null>(null)

export function useChatImagePreview(): ChatImagePreviewContextValue {
  const context = useContext(ChatImagePreviewContext)
  if (!context) {
    return {
      openImagePreview: () => {
        /* noop when used outside provider */
      },
    }
  }
  return context
}

/** Tall / skinny originals that would exceed max height at full column width switch to fixed-height tile + cover (full bleed, clean radius). */
function ChatImagePreviewOverlay({
  urls,
  startIndex,
  onClose,
}: {
  urls: string[]
  startIndex: number
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [currentIndex, setCurrentIndex] = useState(startIndex)

  useEffect(() => {
    setCurrentIndex(startIndex)
  }, [startIndex, urls])

  const currentUrl = urls[currentIndex]

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowLeft' && urls.length > 1) {
        setCurrentIndex(i => Math.max(0, i - 1))
      }
      if (event.key === 'ArrowRight' && urls.length > 1) {
        setCurrentIndex(i => Math.min(urls.length - 1, i + 1))
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, urls.length])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  const goPrevious = () => setCurrentIndex(i => Math.max(0, i - 1))
  const goNext = () => setCurrentIndex(i => Math.min(urls.length - 1, i + 1))

  const imageZoomTransformWrapperReference = useRef<ReactZoomPanPinchRef | null>(null)

  useLayoutEffect(() => {
    imageZoomTransformWrapperReference.current?.resetTransform(0)
  }, [currentIndex, currentUrl])

  const imagePresentationFrameOutlineReference = useRef<HTMLDivElement>(null)
  const [
    tallPortraitUsesCoverCropInFixedHeightTileForFullColumnWidth,
    setTallPortraitUsesCoverCropInFixedHeightTileForFullColumnWidth,
  ] = useState(false)

  useEffect(() => {
    setTallPortraitUsesCoverCropInFixedHeightTileForFullColumnWidth(false)
  }, [currentUrl])

  const recomputeTallPortraitCoverVersusNaturalContainLayout = useCallback(() => {
    const frameElement = imagePresentationFrameOutlineReference.current
    const imageElement = frameElement?.querySelector<HTMLImageElement>(
      '[data-chat-image-preview-slot="primary"]'
    )
    if (!frameElement || !imageElement?.naturalWidth || !imageElement.naturalHeight) return

    const frameWidthPx = frameElement.getBoundingClientRect().width
    if (frameWidthPx <= 0) return

    const { naturalWidth, naturalHeight } = imageElement
    const scaledContentHeightPxIfBitmapSpansFullFrameWidth =
      naturalHeight * (frameWidthPx / naturalWidth)
    const ninetyPercentOfViewportHeightPx = window.innerHeight * 0.9

    const bitmapAtFullFrameWidthWouldExceedVerticalViewportCap =
      scaledContentHeightPxIfBitmapSpansFullFrameWidth > ninetyPercentOfViewportHeightPx + 0.5

    setTallPortraitUsesCoverCropInFixedHeightTileForFullColumnWidth(
      bitmapAtFullFrameWidthWouldExceedVerticalViewportCap
    )
  }, [])

  useLayoutEffect(() => {
    const frameElement = imagePresentationFrameOutlineReference.current
    if (!frameElement) return

    const resizeObserverRecalculatesCoverWhenColumnWidthChanges = new ResizeObserver(() =>
      recomputeTallPortraitCoverVersusNaturalContainLayout()
    )
    resizeObserverRecalculatesCoverWhenColumnWidthChanges.observe(frameElement)
    recomputeTallPortraitCoverVersusNaturalContainLayout()
    const recalculateWhenViewportHeightChanges = () =>
      recomputeTallPortraitCoverVersusNaturalContainLayout()
    window.addEventListener('resize', recalculateWhenViewportHeightChanges)
    return () => {
      resizeObserverRecalculatesCoverWhenColumnWidthChanges.disconnect()
      window.removeEventListener('resize', recalculateWhenViewportHeightChanges)
    }
  }, [currentUrl, recomputeTallPortraitCoverVersusNaturalContainLayout])

  const sharedMaxPreviewHeightCssLiteral = 'min(90dvh, 90vh)'

  const transformWrapperInlineStylesForCurrentLayoutMode: CSSProperties =
    tallPortraitUsesCoverCropInFixedHeightTileForFullColumnWidth
      ? {
          width: '100%',
          maxWidth: '100%',
          height: sharedMaxPreviewHeightCssLiteral,
          minHeight: sharedMaxPreviewHeightCssLiteral,
          maxHeight: sharedMaxPreviewHeightCssLiteral,
        }
      : {
          width: '100%',
          maxWidth: '100%',
          height: 'auto',
          maxHeight: sharedMaxPreviewHeightCssLiteral,
        }

  const transformContentInlineStylesForCurrentLayoutMode: CSSProperties =
    tallPortraitUsesCoverCropInFixedHeightTileForFullColumnWidth
      ? {
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
        }
      : {
          width: '100%',
          maxWidth: '100%',
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
        }

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex flex-col bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={t('chat.imagePreviewDialogLabel')}
    >
      {/* Full-viewport width: counter top-left, close top-right */}
      <header
        className={cn(
          'flex w-full max-w-none shrink-0 flex-row items-start justify-between gap-3 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))]',
          chatImagePreviewEdgeGutterClassName
        )}
      >
        <p className="text-foreground min-w-0 shrink pt-1 text-left text-sm leading-none tabular-nums">
          {urls.length > 1 ? (
            <span>
              {currentIndex + 1} / {urls.length}
            </span>
          ) : null}
        </p>
        <button
          type="button"
          onClick={onClose}
          className={cn('shrink-0', chatImagePreviewControlButtonClassName)}
          aria-label={t('chat.imagePreviewClose')}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </header>

      {/* Matches chat gutter + max-content width; explicit width overrides flex intrinsic sizing */}
      <div
        className={cn(
          'phone-container-padded box-border w-full min-w-0 max-w-full flex-1 shrink self-stretch',
          'flex min-h-0 flex-col items-stretch justify-center',
          'pb-[max(0.75rem,env(safe-area-inset-bottom))]'
        )}
      >
        <div
          ref={imagePresentationFrameOutlineReference}
          className={cn(
            'relative isolate mx-auto box-border w-full min-w-0 max-w-full',
            chatImagePreviewImageFrameRadiusClassName,
            'overflow-hidden bg-muted/10 shadow-sm'
          )}
        >
          <TransformWrapper
            ref={imageZoomTransformWrapperReference}
            initialScale={1}
            minScale={1}
            maxScale={5}
            limitToBounds
            disablePadding
            centerOnInit
            centerZoomedOut={false}
            wheel={{ smoothStep: 0.002 }}
            doubleClick={{ mode: 'reset', animationTime: 280, animationType: 'easeOut' }}
            pinch={{ step: 5 }}
            alignmentAnimation={{
              animationTime: 300,
              animationType: 'easeOut',
            }}
            velocityAnimation={{ disabled: true }}
          >
            <TransformComponent
              wrapperStyle={transformWrapperInlineStylesForCurrentLayoutMode}
              contentStyle={transformContentInlineStylesForCurrentLayoutMode}
              wrapperClass={cn('!box-border min-h-0 overflow-hidden')}
              contentClass={cn('!box-border min-h-0 min-w-0')}
            >
              <img
                data-chat-image-preview-slot="primary"
                src={currentUrl}
                alt=""
                draggable={false}
                decoding="async"
                onLoad={recomputeTallPortraitCoverVersusNaturalContainLayout}
                className={cn(
                  'block w-full min-w-0 select-none',
                  tallPortraitUsesCoverCropInFixedHeightTileForFullColumnWidth
                    ? 'h-full min-h-0 max-h-[min(90dvh,90vh)] object-cover object-center'
                    : 'h-auto max-h-[min(90dvh,90vh)] max-w-[100%] object-contain object-center'
                )}
              />
            </TransformComponent>
          </TransformWrapper>

          {urls.length > 1 && (
            <div className="pointer-events-none absolute inset-x-3 inset-y-0 z-20 flex items-center justify-between">
              <button
                type="button"
                onClick={goPrevious}
                disabled={currentIndex <= 0}
                className={cn('pointer-events-auto', chatImagePreviewControlButtonClassName)}
                aria-label={t('chat.imagePreviewPrevious')}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={goNext}
                disabled={currentIndex >= urls.length - 1}
                className={cn('pointer-events-auto', chatImagePreviewControlButtonClassName)}
                aria-label={t('chat.imagePreviewNext')}
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export function ChatImagePreviewProvider({ children }: { children: React.ReactNode }) {
  const [previewSession, setPreviewSession] = useState<{ urls: string[]; index: number } | null>(
    null
  )

  const openImagePreview = useCallback((args: OpenImagePreviewArgs) => {
    if (!args.urls.length) return
    const index = Math.max(0, Math.min(args.initialIndex ?? 0, args.urls.length - 1))
    setPreviewSession({ urls: args.urls, index })
  }, [])

  const closeImagePreview = useCallback(() => setPreviewSession(null), [])

  const contextValue = useMemo(
    () => ({ openImagePreview }),
    [openImagePreview]
  )

  return (
    <ChatImagePreviewContext.Provider value={contextValue}>
      {children}
      {previewSession && previewSession.urls.length > 0 && (
        <ChatImagePreviewOverlay
          urls={previewSession.urls}
          startIndex={previewSession.index}
          onClose={closeImagePreview}
        />
      )}
    </ChatImagePreviewContext.Provider>
  )
}
